import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

// Hooks
import { useAppDispatch } from "app/hooks";
import useRedirectOnNoAdminPermission from "utils/hooks/useRedirectOnNoAdminPermission";

// Store
import { setToastConfig, toggleLoader, updateDownloadStatus } from "store/slice/UI";
import { clearReportData } from "store/slice/Pace";

// Ability
import { AbilityContext } from "context/Ability";
import { useAbility } from "@casl/react";

// Config
import { tableHeaderConfig } from "./config";

// Enums and Constants
import { urlPaths } from "enums/urlPaths";
import { initialPagination } from "constants/constants";

// Utils
import { debounce, isEmpty, isEqual } from "lodash";
import { useAuth, generateID, forceDownload } from "utils";
import { ConvertedAssessmentCentre, convertUserAssessmentList } from "./adapters/converters";

// Services
import { getTeams, requestProfileSharing, Team } from "services";
import { getAssessmentReportServicePDF } from "services";
import { getPendingInvitations } from "services";

// Types
import { SelectChangeEvent } from "@mui/material";
import { OrderTableBy, OrderTable } from "components/SortMenu/SortMenu";
import {
    TPendingInvitation,
    TProfileSharingPayload,
} from "pages/HubManagement/components/InviteUsers/InviteUsersModal.types";
import {
    fetchAssessmentCentreUsers,
    TAssessmentCentreFiltersPayload,
    updateReminderByUserId,
    updateReminderByUserIdPayload,
} from "services/assessmentCentre";

type Filters = { filter_name: string; filter_pace_status: string[]; filter_teams: string[] };

const DEFAULT_PAGE_SIZE = 24;
const DEFAULT_SORT = { columnId: "pace_timestamp_utc_finished", direction: "desc" } as OrderTableBy;
const FILTERS_INITIAL_VALUE = { filter_name: "", filter_pace_status: ["all"], filter_teams: ["all"] };

const useAssessmentCentre = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // If user changes current hub and doesn't have permissions, redirect somewhere safe
    useRedirectOnNoAdminPermission("SYS_ADMIN_VIEW_PACE_PORTAL", urlPaths.PaceLabsDashboard);

    const { sessionData } = useAuth();
    const orgId = sessionData?.authn.user.org_selected.id;
    const groupId = sessionData?.authn.user.org_selected.group_id;

    const ability = useAbility(AbilityContext);
    const canInviteUsers = ability.can("see", "SAAS_PACE_PORTAL_INVITE_USERS");
    const canNavigateToUserManagement = ability.can("see", "SYS_ADMIN_VIEW_USER_MANAGEMENT");

    const [users, setUsers] = useState<ConvertedAssessmentCentre>({
        data: [],
        meta: initialPagination,
        status: "pristine",
    });

    const [teamsData, setTeamsData] = useState<Team[]>([]);
    const [inputName, setInputName] = useState("");
    const [requestProfileSharingId, setRequestProfileSharingId] = useState<number[]>([]);

    // states used to prefilter results from PACE Dashboard through query params
    const [searchParams, setSearchParams] = useSearchParams();
    const paceStatusFromUrl = searchParams.get("filter_pace_status");

    const [filters, setFilters] = useState<Filters>({
        ...FILTERS_INITIAL_VALUE,
        filter_pace_status: paceStatusFromUrl ? [paceStatusFromUrl] : FILTERS_INITIAL_VALUE.filter_pace_status,
    });

    const [usersSort, setUsersSort] = useState<OrderTableBy>(DEFAULT_SORT);

    const previousFilters = useRef(FILTERS_INITIAL_VALUE);

    const [inputPage, setInputPage] = useState<number | string>(1);
    const previousPageValue = useRef<number | string>(1);
    const paginationTextInputRef = useRef<any>(null);

    const disabledReminderButton = !users.data.some((user) => user.checked);
    const disableCheckAllUsers = !users.data.some((user) => user.paceStatus === "Incomplete");
    const areAllImcompleteUsersChecked =
        users.data.some((user) => user.paceStatus === "Incomplete") &&
        users.data.filter((user) => user.paceStatus === "Incomplete").every((user) => user.checked);
    const areSomeImcompleteUsersChecked =
        !areAllImcompleteUsersChecked && users.data.some((user) => user.paceStatus === "Incomplete" && user.checked);

    const disableAll = users.status === "fetching";
    const disableClearAll =
        disableAll ||
        Boolean(
            !Object.values(filters)
                .flat()
                .filter((el) => typeof el === "string" && el !== "" && el.toLowerCase() !== "all").length
        );

    // Invitations
    const [activeTab, setActiveTab] = useState<number>(0);
    const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
    const [pendingInvitations, setPendingInvitations] = useState<TPendingInvitation[]>([]);

    const handleRequestSharing = async (userId: number, user_name: string) => {
        const requestPayload: TProfileSharingPayload = {
            invite_list: [
                {
                    invitee_user_id: userId,
                    org_id: orgId,
                },
            ],
        };

        try {
            setRequestProfileSharingId((prev) => [...prev, userId]);

            const response = await requestProfileSharing(requestPayload);
            if (response.status === 200) {
                dispatch(
                    setToastConfig({
                        title: "Request Sent",
                        content: `An email has been sent to ${user_name} to ask to share their PACE scores with your hub.`,
                    })
                );
            }
        } catch (error) {
            console.error(error);
        } finally {
            setRequestProfileSharingId((prev) => prev.filter((id) => id !== userId));
        }
    };

    const fetchPendingInvitations = async () => {
        try {
            const { data } = await getPendingInvitations(Number(orgId));

            const pendingWithoutInvite: TPendingInvitation[] = data.pending_users_without_invite.map((user) => ({
                id: user.id,
                recipient_name: user.name,
                recipient_email: user.email,
            }));

            setPendingInvitations([...data.org_invites_sent, ...pendingWithoutInvite]);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchTeams = async () => {
        try {
            const { data } = await getTeams();
            const teamsWithIds = data.teams.map((team) => ({
                ...team,
                id: generateID(),
                name: team.name,
            }));

            setTeamsData(teamsWithIds);
        } catch (error) {
            console.error(error);
        }
    };

    const handleFetchData = async (params?: Partial<TAssessmentCentreFiltersPayload>) => {
        if (isEqual(filters, previousFilters.current) && isEmpty(params)) {
            return;
        }

        const isDefaultPaceStatus = params?.filter_pace_status?.some((val) => val === "all");
        const isDefaultTeams = params?.filter_teams?.some((val) => val === "all");

        const requestPayload = {
            ...(params?.filter_name ? { filter_name: params.filter_name } : {}),
            ...(isDefaultPaceStatus ? {} : { filter_pace_status: params?.filter_pace_status }),
            ...(isDefaultTeams ? {} : { filter_teams: params?.filter_teams }),
            ...(params?.order_by ? { order_by: params.order_by } : {}),
            page_no: params?.page_no ? params.page_no : 1,
            page_size: DEFAULT_PAGE_SIZE,
        };

        try {
            setUsers((users) => ({ ...users, status: "fetching" }));
            const { data } = await fetchAssessmentCentreUsers(requestPayload);
            const convertedData = convertUserAssessmentList(data.results);
            setUsers({ data: convertedData, meta: data.meta, status: "done" });
            previousFilters.current = isEmpty(params) ? filters : FILTERS_INITIAL_VALUE;
        } catch (error) {
            console.error(error);
            setUsers((users) => ({ ...users, status: "error" }));
        }
    };

    const handleGoToUserManagement = () => {
        navigate(urlPaths.UserManagement);
    };

    // --------------------------------------------------------------------------------------------------------------
    // Filters and Sorting Functions
    // --------------------------------------------------------------------------------------------------------------
    const delayedFilterName = useRef(
        debounce((value: string, filters: Filters, usersSort: OrderTableBy) => {
            handleFetchData({
                ...filters,
                filter_name: value,
                order_by: `${usersSort.columnId},${usersSort.direction}`,
            });
        }, 500)
    ).current;
    useEffect(() => () => delayedFilterName.cancel(), [delayedFilterName]);

    const handleChangeFilters = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[] | number[]>
    ) => {
        setInputPage(1);

        // Filter by user name
        if (event.target.name === "user-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;

            setInputName(newValue);
            setFilters((currentVal) => ({ ...currentVal, filter_name: newValue }));

            delayedFilterName(newValue, filters, usersSort);
        }

        // Filter by pace status or teams
        if (event.target.value && event.target.name !== "user-filter") {
            const filterName = event.target.name;
            let newValue = event.target.value as string[];

            if ((newValue.length > 1 && newValue.at(-1) === "all") || !newValue.length) {
                handleClearFilters(filterName as Parameters<typeof handleClearFilters>[0]);
                return;
            }

            newValue = newValue.filter((value) => value !== "all");
            const newFilters = { ...filters, [filterName]: newValue };

            setFilters(newFilters);
            handleFetchData({ ...newFilters, order_by: `${usersSort.columnId},${usersSort.direction}` });
        }
    };

    const handleClearFilters = (filterName?: "filter_pace_status" | "filter_teams" | "all") => {
        if (filterName === "all") {
            setInputName("");
            setFilters(FILTERS_INITIAL_VALUE);
            handleFetchData({
                ...FILTERS_INITIAL_VALUE,
                order_by: `${DEFAULT_SORT.columnId},${DEFAULT_SORT.direction}`,
            });
            return;
        }

        if (filterName) {
            const newFilters = { ...filters, [filterName]: ["all"] };
            setFilters(newFilters);
            handleFetchData({ ...newFilters, order_by: `${usersSort.columnId},${usersSort.direction}` });
        }
    };

    const handleSort = (direction: OrderTable, columnId: string | number) => {
        setUsersSort({ columnId, direction });
        handleFetchData({
            ...filters,
            page_size: users.meta.page_size,
            page_no: typeof inputPage === "string" ? parseInt(inputPage || "1") : inputPage,
            order_by: `${columnId},${direction}`,
        });
    };

    // --------------------------------------------------------------------------------------------------------------
    // Other Functions
    // --------------------------------------------------------------------------------------------------------------
    const toggleCheckUser = (event: React.ChangeEvent<HTMLInputElement>, userId: number) => {
        setUsers((users) => ({
            ...users,
            data: users.data.map((user) => ({
                ...user,
                checked: user.id === userId ? event.target.checked : user.checked,
            })),
        }));
    };

    const toggleCheckAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        setUsers((users) => ({
            ...users,
            data: users.data.map((user) =>
                user.paceStatus === "Incomplete"
                    ? {
                          ...user,
                          checked: isChecked,
                      }
                    : user
            ),
        }));
    };

    const handleSendReminder = async (userId?: number) => {
        if (!orgId) {
            console.error("Invalid Organisation Id.");
            return;
        }

        const remindersToSend: updateReminderByUserIdPayload["invite_list"] = userId
            ? users.data
                  .filter((user) => user.id === userId)
                  .map((user) => ({
                      invitee_user_id: user.id,
                      org_id: orgId,
                  }))
            : users.data
                  .filter((user) => user.checked && user.paceStatus === "Incomplete")
                  .map((user) => ({
                      invitee_user_id: user.id,
                      org_id: orgId,
                  }));

        try {
            await updateReminderByUserId({ invite_list: remindersToSend });

            fetchPendingInvitations();
            handleFetchData({
                ...filters,
                order_by: `${usersSort.columnId},${usersSort.direction}`,
                page_no: users.meta.page_no || 1,
            });
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (inviteModalOpen) {
            fetchPendingInvitations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteModalOpen]);

    // --------------------------------------------------------------------------------------------------------------
    // Pagination functions
    // --------------------------------------------------------------------------------------------------------------
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > users.meta.pages) {
            return;
        }
        setInputPage(page);
        handleFetchData({
            ...filters,
            order_by: `${usersSort.columnId},${usersSort.direction}`,
            page_size: users.meta.page_size,
            page_no: page,
        });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setInputPage("");
            return;
        }
        const inputValue = Number(event.target.value);
        if (inputValue > 0 && inputValue <= users.meta.pages) {
            setInputPage(inputValue);
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputPage === users.meta.page_no) {
                return;
            }

            paginationTextInputRef.current.blur();
        }

        if (event.key === "Escape") {
            setInputPage(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };

    const handleOnBlur = () => {
        if (inputPage === "" || inputPage === users.meta.page_no) {
            setInputPage(users.meta.page_no);
            return;
        }

        if (Boolean(inputPage) && Number(inputPage) > 0 && Number(inputPage) <= users.meta.pages) {
            previousPageValue.current = inputPage;
            handlePageChange(Number(inputPage));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // User row actions
    // --------------------------------------------------------------------------------------------------------------

    const handleShowReport = (event: React.MouseEvent<HTMLElement, MouseEvent>, userId: number) => {
        event.stopPropagation();
        const user = users.data.find((item) => item.id === userId);

        if (!user?.assessments) {
            return;
        }
        const id = user?.assessments[0].id;
        dispatch(clearReportData());
        navigate(`/pace/report/user/${userId}/assessment/${id}`);
    };

    const handleDownloadReport = async (event: React.MouseEvent<HTMLElement, MouseEvent>, userId: number) => {
        try {
            event.stopPropagation();
            const user = users.data.find((item) => item.id === userId);

            if (!user?.assessments) {
                return;
            }

            const id = user?.assessments[0].id;
            dispatch(updateDownloadStatus({ loading: true, error: false }));
            const pdfResponse = await getAssessmentReportServicePDF(userId, id, groupId);

            forceDownload(pdfResponse, "PACE_Report.pdf");
            dispatch(updateDownloadStatus({ loading: false, error: false }));
        } catch (error) {
            console.error(error);

            dispatch(updateDownloadStatus({ loading: false, error: true }));
            setTimeout(() => {
                dispatch(updateDownloadStatus({ loading: false, error: false }));
            }, 5000);
        }
    };

    const handleTransferProfile = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => {
        ev.preventDefault();
    };

    const handleGetInfo = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => {
        ev.preventDefault();
    };

    // Invitations
    const handleOpenInvitationModal = () => {
        setActiveTab(1);
        setInviteModalOpen(true);
    };

    const handleInviteSelectedUsers = () => {
        setActiveTab(0);
        setInviteModalOpen(true);
    };

    const closeInviteModal = () => {
        setInviteModalOpen(false);
    };

    // --------------------------------------------------------------------------------------------------------------
    // Initial Fetch
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const initialFetch = async () => {
            dispatch(toggleLoader(true));

            // If user changes current selected hub, reset filters and pagination related states and refs
            previousPageValue.current = 1;
            previousFilters.current = FILTERS_INITIAL_VALUE;

            setInputPage(1);
            setInputName("");
            setUsersSort(DEFAULT_SORT);
            setFilters(FILTERS_INITIAL_VALUE);
            // ---------------------------------------------------

            if (canInviteUsers) {
                fetchPendingInvitations();
            }

            if (!teamsData.length) {
                fetchTeams();
            }

            try {
                const { data } = await fetchAssessmentCentreUsers({
                    page_size: DEFAULT_PAGE_SIZE,
                    order_by: `${usersSort.columnId},${usersSort.direction}`,
                    ...(filters.filter_pace_status.length > 0 && !filters.filter_pace_status.includes("all")
                        ? { filter_pace_status: filters.filter_pace_status }
                        : {}),
                });
                const convertedData = convertUserAssessmentList(data.results);
                setUsers({ ...data, data: convertedData, status: "done" });
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(toggleLoader(false));
            }
        };

        initialFetch();

        // Clean query param from url
        if (paceStatusFromUrl) {
            setSearchParams({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    return {
        users,
        filters,
        teamsData,
        usersSort,
        tableHeader: tableHeaderConfig,
        inputName,
        inputPage,
        paginationTextInputRef,
        disableAll,
        disableClearAll,
        filtersInitialValue: FILTERS_INITIAL_VALUE,
        activeTab,
        inviteModalOpen,
        pendingInvitations,
        orgId,
        disabledReminderButton,
        disableCheckAllUsers,
        areSomeImcompleteUsersChecked,
        areAllImcompleteUsersChecked,
        canInviteUsers,
        canNavigateToUserManagement,
        requestProfileSharingId,
        handleFetchData,
        handleSort,
        handleChangeFilters,
        handleClearFilters,
        toggleCheckUser,
        toggleCheckAllUsers,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        handleShowReport,
        handleDownloadReport,
        handleTransferProfile,
        handleGetInfo,
        handleSendReminder,
        handleOpenInvitationModal,
        handleInviteSelectedUsers,
        closeInviteModal,
        handleGoToUserManagement,
        handleRequestSharing,
    };
};

export default useAssessmentCentre;
